/**
 * Modelo de permissões efetivo dos perfis padrão (TASK-15, REQ-05/REQ-06/REQ-08).
 *
 * Constrói, a partir de `contracts.ts` (catálogo + perfis padrão + deltas), o alcance efetivo
 * de cada (perfil, permissão) — a mesma responsabilidade do `PermissionResolver` em
 * `Azim.Authorization` (TASK-14), reimplementada em TypeScript contra os MESMOS três JSONs.
 */
import { DEFAULT_PROFILES, DEFAULT_PROFILE_DELTAS, PERMISSION_CATALOG } from "./contracts.js";
import type { DefaultProfileDelta, Permission, Reach, RoleKey } from "./types.js";

export const REACH_ORDER: Readonly<Record<Reach, number>> = {
  none: 0,
  own: 1,
  unit: 2,
  org: 3,
};

/**
 * Permissões cross-tenant: fora da regra de auto-resolução do Administrador (REQ-04 §2.4).
 * `platform.manage_tenants` é a única do catálogo (área `platform`, ADR-0044) — resolvida só
 * para `platform_operator`, nunca para `tenant_admin`, mesmo que ele "resolva tudo com org".
 * Este flag não existe no schema do catálogo (não é derivável de `supported_reaches`,
 * `grantable` nem de nenhum outro campo — verificado lendo `permission-catalog.json`: outras
 * permissões não-`grantable` COMO `migration.execute` recebem `org` para o Administrador na
 * `matrix.md`); é uma exceção nomeada, documentada em `default-profiles.json` na nota do
 * perfil `tenant_admin` ("exceto as cross-tenant") e confirmada por `docs/product/authz/
 * matrix.md` (linha `platform.manage_tenants`, coluna Administrador = `—`).
 */
const CROSS_TENANT_PERMISSION_KEYS: ReadonlySet<string> = new Set(["platform.manage_tenants"]);

function indexPermissions(): ReadonlyMap<string, Permission> {
  const map = new Map<string, Permission>();
  for (const permission of PERMISSION_CATALOG.permissions) {
    map.set(permission.key, permission);
  }
  return map;
}

const PERMISSIONS_BY_KEY = indexPermissions();

/** Ordena os deltas topologicamente por `requires` (dependências antes de dependentes). */
function topologicallySortDeltas(): readonly DefaultProfileDelta[] {
  const byId = new Map(DEFAULT_PROFILE_DELTAS.map((delta) => [delta.id, delta]));
  const visited = new Set<string>();
  const ordered: DefaultProfileDelta[] = [];

  function visit(id: string, chain: readonly string[]): void {
    if (visited.has(id)) return;
    if (chain.includes(id)) {
      throw new Error(`Ciclo de 'requires' entre deltas de perfil padrão envolvendo '${id}'.`);
    }
    const delta = byId.get(id);
    if (!delta) return; // requires apontando para delta inexistente: ignorado aqui (verify-default-profiles.mjs cobre isso).
    for (const dependencyId of delta.requires) {
      visit(dependencyId, [...chain, id]);
    }
    visited.add(id);
    ordered.push(delta);
  }

  for (const delta of DEFAULT_PROFILE_DELTAS) {
    visit(delta.id, []);
  }
  return ordered;
}

/** Alcance base (versão 1, antes de deltas) para todo (perfil, permissão) — de `grants[]`. */
function buildBaseGrants(): Map<string, Reach> {
  const map = new Map<string, Reach>();
  for (const grant of DEFAULT_PROFILES.grants) {
    map.set(`${grant.role_key}\u0000${grant.permission_key}`, grant.reach);
  }
  return map;
}

/** Aplica os deltas, em ordem topológica, sobre o alcance base. */
function buildEffectiveGrants(): Map<string, Reach> {
  const grants = buildBaseGrants();
  for (const delta of topologicallySortDeltas()) {
    for (const change of delta.changes) {
      const key = `${change.role_key}\u0000${change.permission_key}`;
      // Modelo de PERFIL PADRÃO (não de tenant específico): não há noção de "perfil
      // modificado pelo Administrador" aqui — sempre aplicamos a mudança do delta. O drift
      // por tenant (perfil já modificado, ou alcance atual diferente de reach_from) é
      // responsabilidade do `DefaultProfileDeltaApplier` no backend (TASK-16), que opera sobre
      // o estado gravado por tenant; este módulo só materializa "o que o catálogo padrão diz".
      grants.set(key, change.reach_to);
    }
  }
  return grants;
}

const EFFECTIVE_GRANTS = buildEffectiveGrants();

/** Maior alcance suportado por uma permissão (usado pela auto-resolução do Administrador). */
function maxSupportedReach(permission: Permission): Reach {
  if (permission.supported_reaches.includes("org")) return "org";
  if (permission.supported_reaches.includes("unit")) return "unit";
  if (permission.supported_reaches.includes("own")) return "own";
  return "none";
}

function resolveTenantAdminReach(permission: Permission): Reach {
  if (CROSS_TENANT_PERMISSION_KEYS.has(permission.key)) return "none";
  return maxSupportedReach(permission);
}

/**
 * Resolve o alcance efetivo de `role` para `permissionKey`. Permissão inexistente ou retirada
 * (`retired: true`) resolve sempre `"none"` — nunca lança (contrato do `canDo`, PBT).
 */
export function resolveReach(role: RoleKey, permissionKey: string): Reach {
  const permission = PERMISSIONS_BY_KEY.get(permissionKey);
  if (!permission || permission.retired) return "none";
  if (role === "tenant_admin") return resolveTenantAdminReach(permission);
  const key = `${role}\u0000${permissionKey}`;
  return EFFECTIVE_GRANTS.get(key) ?? "none";
}

export function getPermission(permissionKey: string): Permission | undefined {
  return PERMISSIONS_BY_KEY.get(permissionKey);
}

export function listPermissions(): readonly Permission[] {
  return PERMISSION_CATALOG.permissions;
}

/**
 * Coerência de alcance derivado (REQ-04, achado 9 — `contracts/authz/v1/golden/
 * req04-coherence.golden.json`): para toda permissão derivada `d` com `derives_from` contendo
 * `s`, `reach(d) <= reach(s)`; se `reach(s) = "own"` e `d` não suporta `own` (`permission.own`
 * do catálogo — não `supported_reaches`, ver nota abaixo), o teto efetivo dessa fonte cai para
 * `"none"`. O teto final é o mínimo entre os tetos de todas as fontes.
 *
 * Nota sobre `derivedSupportsOwn`: usa o campo `own` do catálogo (responsável medido no
 * inventário), NÃO `supported_reaches.includes("own")`. `report.view` é o caso que desambiguou
 * isso: `supported_reaches` inclui `"own"`, mas `own: false` — e é exatamente esse o permission
 * usado no vetor de ouro `fonte_own_derivada_sem_suporte_a_own_vira_none`/`..._correta_em_none`
 * com `derived_supports_own: false`. Ou seja, a coerência usa `permission.own`.
 */
export function isReachCoherent(params: {
  readonly reachDerived: Reach;
  readonly sourceReaches: readonly Reach[];
  readonly derivedSupportsOwn: boolean;
}): boolean {
  const { reachDerived, sourceReaches, derivedSupportsOwn } = params;
  let ceiling = REACH_ORDER.org;
  for (const sourceReach of sourceReaches) {
    const effectiveSourceCeiling =
      sourceReach === "own" && !derivedSupportsOwn ? REACH_ORDER.none : REACH_ORDER[sourceReach];
    ceiling = Math.min(ceiling, effectiveSourceCeiling);
  }
  return REACH_ORDER[reachDerived] <= ceiling;
}
